import { FfprobeStreamObject } from 'src/api/services/MediaClient/MediaClient.types';

/**
 * AI analysis state for media items
 */
export type AnalyzeState = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'ABORTED';

// Instead of extending ApiMediaObject, which causes type conflicts,
// we'll just include the relevant API properties and add our extensions
export interface MediaObject {
  id: string;
  file_name?: string;
  file_path?: string;
  name?: string;
  mime_type?: string;
  size: number;
  directory_id?: string | null;
  created_at: string;
  updated_at?: string;
  description?: string;
  comment?: string;
  collection_name?: string;
  custom_properties?: Partial<FfprobeStreamObject> | Record<string, any>;
  index_url?: string;
  url?: string;
  thumbnail_url?: string;
  tags?: string[] | null;
  path?: string[];
  userPath?: string[];
  agent_id?: string;
  is_folder?: boolean;
  // AI-generated analysis fields
  generated_description?: string;
  generated_tags?: string[];
  analyze_state?: AnalyzeState;
  // Video properties from the API
  video?: {
    id: string;
    media_id: string;
    provider_name: string;
    provider_id: string;
    status: 'pending' | 'generating' | 'generated' | 'failed';
    created_at: string;
    updated_at: string;
    error?: {
      message: string;
      code: string;
      data?: any;
    };
    dash?: string;
    hls?: string;
    thumbnail_url?: string;
  };
  // These properties are marked as deprecated in the API but still used in the code
  representations?: any[];
  generated_resolutions?: Partial<Record<string | number, boolean>>;
  generated_conversions?: Partial<Record<string, string>>;
  // Metadata with flexible type support for tags
  metadata?: {
    description?: string;
    copyright?: string;
    author?: string;
    location?: string;
    tags?: string | string[];
    [key: string]: any;
  } | any[];
}

// Directory structure definition
export interface MediaDirectory {
  id: string;
  name: string;
  parent_id?: string | null;
  parent_directory_id?: string | null;
  created_at?: string;
  updated_at?: string;
  path?: string;
  full_path?: string;
  display_path?: string;
  custom_properties?: {
    apiPath?: string;
    displayPath?: string;
    parentPath?: string;
    isNestedDirectory?: boolean;
    path?: string;
    fullPath?: string | string[];
    cleanPath?: string | string[];
  } | Record<string, any>;
  // Children directories for hierarchical structure
  children?: MediaDirectory[];
}

// API response structure
export interface MediaResponse {
  folders: any[];
  files: MediaObject[];
  currentPath: any[];
  // Custom properties for preserving files during filtering
  _rawFiles?: MediaObject[];
  __forcePreserveFiles?: boolean;
}

/** Default per-request upload limit in bytes (100 GB). Used as fallback when the server does not provide one. */
export const DEFAULT_PER_REQUEST_LIMIT = 100 * 1024 * 1024 * 1024;

// Storage usage status interface
export interface StorageUsageStatus {
  used_storage: number; // in bytes
  max_storage: number; // in bytes
  percentage_used: number; // 0-100
  fileCount?: number; // number of files
  perRequestLimit?: number; // per-request upload limit in bytes for soft warnings
}

// Interface matching the actual API response format for storage status
export interface StorageStatusResponse {
  global?: {
    sizeUsed: number;
    sizeLimit: number;
    sizeRemaining: number;
    fileCount: number;
  };
  sizeUsed: number;
  sizeLimit: number;
  sizeRemaining: number;
  fileCount: number;
  perRequestLimit?: number; // per-request upload limit in bytes for soft warnings
}

// Helper type for API responses that may have different field names
export interface ApiMediaFile extends MediaObject {
  // Sometimes the API returns 'name' instead of 'file_name'
  name?: string;
}

// ============================================================================
// Media Processing & Error Utilities
// ============================================================================

/**
 * Known error codes from the backend that indicate processing state
 * Using error codes is more reliable than message matching
 */
export const PROCESSING_ERROR_CODES = {
  FILE_NOT_READY: 'FILE_NOT_READY',
  MEDIA_PROCESSING: 'MEDIA_PROCESSING',
  TRANSCODING_IN_PROGRESS: 'TRANSCODING_IN_PROGRESS',
  ANALYSIS_IN_PROGRESS: 'ANALYSIS_IN_PROGRESS',
} as const;

/**
 * HTTP status codes that may indicate processing state
 */
export const PROCESSING_HTTP_CODES = [409, 423] as const; // Conflict, Locked

/**
 * Extract error message from various error response formats
 * Centralizes the error message extraction logic to avoid duplication
 * 
 * @param error - The error object (typically from axios)
 * @returns The extracted error message string
 */
export const extractErrorMessage = (error: any): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    ''
  );
};

/**
 * Extract error code from various error response formats
 * 
 * @param error - The error object (typically from axios)
 * @returns The extracted error code or null
 */
export const extractErrorCode = (error: any): string | null => {
  return (
    error?.response?.data?.code ||
    error?.response?.data?.error_code ||
    error?.code ||
    null
  );
};

/**
 * Extract HTTP status code from error
 * 
 * @param error - The error object (typically from axios)
 * @returns The HTTP status code or null
 */
export const extractHttpStatus = (error: any): number | null => {
  return error?.response?.status || null;
};

/**
 * Check if an error indicates a file is still being processed
 * Uses a multi-layered approach for robustness:
 * 1. First checks for known error codes (most reliable)
 * 2. Then checks HTTP status codes
 * 3. Falls back to message substring matching (least reliable but catches edge cases)
 * 
 * @param error - The error object or error message string
 * @returns true if the error indicates the file is still processing
 */
export const isProcessingError = (error: any): boolean => {
  // Handle string input for backward compatibility
  if (typeof error === 'string') {
    return isProcessingErrorMessage(error);
  }

  // 1. Check for known error codes (most reliable)
  const errorCode = extractErrorCode(error);
  if (errorCode) {
    const knownCodes = Object.values(PROCESSING_ERROR_CODES);
    if (knownCodes.includes(errorCode as any)) {
      return true;
    }
  }

  // 2. Check HTTP status codes
  const httpStatus = extractHttpStatus(error);
  if (httpStatus && PROCESSING_HTTP_CODES.includes(httpStatus as any)) {
    return true;
  }

  // 3. Fall back to message matching (least reliable but catches edge cases)
  const errorMessage = extractErrorMessage(error);
  return isProcessingErrorMessage(errorMessage);
};

/**
 * Check if an error message string indicates processing state
 * This is the fallback method when error codes aren't available
 * 
 * @param message - The error message to check
 * @returns true if the message indicates processing state
 */
const isProcessingErrorMessage = (message: string): boolean => {
  if (!message) return false;
  const lowerMessage = message.toLowerCase();
  return (
    lowerMessage.includes('not ready') ||
    lowerMessage.includes('still processing') ||
    lowerMessage.includes('being processed') ||
    lowerMessage.includes('processing in progress') ||
    lowerMessage.includes('file is processing') ||
    lowerMessage.includes('transcoding')
  );
};

/**
 * Check if a media file is currently being processed
 * Checks all processing states: video transcoding, AI analysis, and upload status
 * 
 * @param media - The media object to check
 * @param uploadStatus - Optional upload status from upload context
 * @returns true if the file is still being processed in any way
 */
export const isMediaProcessing = (
  media: MediaObject,
  uploadStatus?: 'pending' | 'in-progress' | 'uploaded' | 'failed'
): boolean => {
  // Check video transcoding state
  const isVideoProcessing = 
    media.video?.status === 'pending' || 
    media.video?.status === 'generating';
  
  // Check AI analysis state
  const isAnalyzing = 
    media.analyze_state === 'PENDING' || 
    media.analyze_state === 'IN_PROGRESS';
  
  // Check upload state if provided
  const isUploading = 
    uploadStatus === 'pending' || 
    uploadStatus === 'in-progress';
  
  return isVideoProcessing || isAnalyzing || isUploading;
};

/**
 * Get a human-readable processing status message for a media file
 * Returns the most relevant status message based on current processing state
 * 
 * @param media - The media object to check
 * @param uploadStatus - Optional upload status from upload context
 * @returns A status message or null if not processing
 */
export const getMediaProcessingStatus = (
  media: MediaObject,
  uploadStatus?: 'pending' | 'in-progress' | 'uploaded' | 'failed'
): string | null => {
  // Check upload state first (highest priority)
  if (uploadStatus === 'pending') {
    return 'Upload pending';
  }
  if (uploadStatus === 'in-progress') {
    return 'Upload in progress';
  }
  
  // Check video processing state
  if (media.video?.status === 'pending') {
    return 'Video processing pending';
  }
  if (media.video?.status === 'generating') {
    return 'Video transcoding in progress';
  }
  
  // Check AI analysis state
  if (media.analyze_state === 'PENDING') {
    return 'AI analysis pending';
  }
  if (media.analyze_state === 'IN_PROGRESS') {
    return 'AI analysis in progress';
  }
  
  return null;
};

/**
 * Check if a media file can be safely deleted
 * This is the inverse of isMediaProcessing - returns true when deletion is safe
 * 
 * @param media - The media object to check
 * @param uploadStatus - Optional upload status from upload context
 * @returns true if the file can be safely deleted
 */
export const canDeleteMedia = (
  media: MediaObject,
  uploadStatus?: 'pending' | 'in-progress' | 'uploaded' | 'failed'
): boolean => {
  return !isMediaProcessing(media, uploadStatus);
};

/**
 * Machine-readable message the API returns when a delete is refused because a
 * video request still references the media. Never shown to the client — the
 * API and this app do not deploy together, so this string has to stay stable
 * while the client-facing copy below changes freely.
 */
export const MEDIA_IN_USE_ERROR_MESSAGE = 'Media is used as RequestAssets';

/**
 * True when a failed delete was refused because the media is referenced by a
 * video request.
 */
export const isMediaInUseError = (error: any): boolean =>
  extractErrorMessage(error).includes(MEDIA_IN_USE_ERROR_MESSAGE);

/**
 * Title of the video request holding the media, when the API sent one. Older
 * API builds only send `message`, so this is optional by design.
 */
export const extractInUseRequestTitle = (error: any): string | null => {
  const title = error?.response?.data?.request_title;

  return typeof title === 'string' && title.trim() !== '' ? title.trim() : null;
};

/**
 * What to call the blocked item in client-facing copy. The Library holds
 * images, audio and documents next to video, and the API refuses a delete for
 * any of them, so the copy must not call a logo a video. Falls back to "file",
 * which is never wrong, when the type cannot be established at all.
 */
export const getMediaNoun = (media?: (MediaObject & { file_type?: string }) | null): string => {
  // Resolved the way the rest of the directory resolves it: lower-cased
  // `includes` on the mime type (the media-grid tab filters), `file_type` as
  // the documented second source (media-item-detail), then the filename
  // extension (media-card). The Library routinely holds rows whose mime_type
  // is missing or `application/octet-stream`, and those are exactly the rows
  // this copy would otherwise call a "file".
  const mimeType = (media?.mime_type || media?.file_type || '').toLowerCase();
  const fileName = media?.file_name ?? '';

  if (mimeType.includes('video') || /\.(mp4|m4v|webm|mov|avi)$/i.test(fileName)) {
    return 'video';
  }
  if (mimeType.includes('image') || /\.(jpe?g|png|gif|svg|webp)$/i.test(fileName)) {
    return 'image';
  }
  if (mimeType.includes('audio') || /\.(mp3|wav|ogg|m4a)$/i.test(fileName)) {
    return 'audio file';
  }

  return 'file';
};

/**
 * Client-facing copy for a refused delete.
 *
 * The previous wording said the media was used in an "ongoing" video request.
 * The lock is not status-based — it holds on Approved and Completed requests
 * too — so clients read "ongoing", waited for the request to finish, and found
 * the file still undeletable. Naming the request tells them where the file
 * actually is.
 */
export const buildMediaInUseMessage = (
  requestTitle?: string | null,
  media?: MediaObject | null,
): string => {
  const noun = getMediaNoun(media);

  return requestTitle
    ? `This ${noun} is used in the video request "${requestTitle}", so it can't be deleted from your Library.`
    : `This ${noun} is used in a video request, so it can't be deleted from your Library.`;
};

/**
 * Plural variant, for bulk deletes where several files were refused.
 */
export const MEDIA_IN_USE_BULK_MESSAGE =
  "Some of the selected files are used in video requests, so they can't be deleted from your Library.";

/**
 * Bulk deletes where only *some* failures were in-use rejections and the rest
 * failed for another reason (a file still transcoding, say).
 *
 * These need wording of their own rather than falling through to the generic
 * branch, which de-camel-cases whatever the API said and would render
 * `MEDIA_IN_USE_ERROR_MESSAGE` — an internal string — into the client's toast.
 */
export const MEDIA_IN_USE_MIXED_MESSAGE =
  "Some of the selected files are used in video requests, so they can't be deleted from your Library. The rest could not be deleted — please try those again.";

/**
 * The mixed case where every non-in-use failure was a file still being
 * processed. Kept separate because "wait a moment and try again" is the one
 * actionable thing the client can do, and folding it into the generic mixed
 * wording above would lose it.
 */
export const MEDIA_IN_USE_MIXED_PROCESSING_MESSAGE =
  "Some of the selected files are used in video requests, so they can't be deleted from your Library. The rest are still being processed — please wait a moment and try those again.";
